// src/components/table/TripTable.tsx
import React, { useEffect } from 'react';
import { 
  Box, 
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { TaskInfo, SortOption, Trip, TouchPoint, TouchPointHoverPreview } from '../../models/types';
import TripCard from './TripCard';
import { getTripWindowMetrics } from '../../utils/formatters';
import CompactSearchSortControl, { CompactSortOption } from '../common/CompactSearchSortControl';
import { canBuildTimelineStripData, hasTimelineSupport } from '../timeline/timelineUtils';

interface TripTableProps {
  taskInfo: TaskInfo | null;
  selectedTrips: string[];
  visibleTrips: string[];
  searchTerm: string;
  sortConfig: {
    key: SortOption;
    direction: 'asc' | 'desc';
  };
  onTripSelect: (tripName: string) => void;
  onTripVisibility: (tripName: string) => void;
  onSearch: (term: string) => void;
  onSortKeyChange: (key: SortOption) => void;
  onSortDirectionChange: (direction: 'asc' | 'desc') => void;
  allVisible?: boolean;
  onToggleAllVisibility?: (showAll: boolean) => void;
  onFocusLngLat?: (lngLat: [number, number]) => void;
  onFocusTrip?: (tripName: string) => void;
  onFocusTouchPoint?: (tp: TouchPoint) => void;
  onHoverTouchPoint?: (preview: TouchPointHoverPreview | null) => void;
  editMode?: boolean;
  editSourceTrip?: string | null;
  selectedEditTouchPointIds?: string[];
  onToggleEditTouchPoint?: (touchPoint: TouchPoint) => void;
  showTableTimelines?: boolean;
  onToggleTableTimelines?: (enabled: boolean) => void;
  onOpenTimeline?: (tripName: string) => void;
  onTimelineOrderChange?: (tripNames: string[]) => void;
  highlightedTripName?: string | null;
  onStartTripEdit?: (tripName: string) => void;
  onCancelTripEdit?: () => void;
}

const TripTable: React.FC<TripTableProps> = ({
  taskInfo,
  selectedTrips,
  visibleTrips,
  searchTerm,
  sortConfig,
  onTripSelect,
  onTripVisibility,
  onSearch,
  onSortKeyChange,
  onSortDirectionChange,
  allVisible = true,
  onToggleAllVisibility,
  onFocusLngLat,
  onFocusTrip,
  onFocusTouchPoint,
  onHoverTouchPoint,
  editMode = false,
  editSourceTrip = null,
  selectedEditTouchPointIds = [],
  onToggleEditTouchPoint,
  showTableTimelines = false,
  onToggleTableTimelines,
  onOpenTimeline,
  onTimelineOrderChange,
  highlightedTripName = null,
  onStartTripEdit,
  onCancelTripEdit,
}) => {
  const isGemba = taskInfo?.tenant?.toLowerCase() === 'gemba';
  const timelineSupported = hasTimelineSupport(taskInfo);

  const getTouchPointCount = (trip: Trip): number => {
    if (Array.isArray(trip.tpDetails) && trip.tpDetails.length > 0) {
      return trip.tpDetails.length;
    }
    const fallback = (trip as any).stopCount;
    return Number.isFinite(fallback) ? Number(fallback) : 0;
  };

  // Filter trips based on search term
  const searchLower = searchTerm.toLowerCase().trim();
  let filteredTrips = (taskInfo?.trips ?? []).filter(trip => {
    if (!searchLower) return true;
    const matchesTripId =
      trip.name.toLowerCase().includes(searchLower) ||
      String(trip.vehTripNo ?? '').includes(searchLower) ||
      (trip.tripMeta?.loadId ?? '').toLowerCase().includes(searchLower);
    if (matchesTripId) return true;
    return trip.tpDetails.some(tp => {
      const fields = [
        tp.name,
        tp.orderNumber,
        tp.customerName,
        tp.generatorName,
        tp.tpId,
      ];
      return fields.some(
        field => field && field.toLowerCase().includes(searchLower)
      );
    });
  });

  const resolveSortMetric = (trip: Trip, key: SortOption): string | number => {
    const fromPayload = trip.sortValues?.[key];
    if (typeof fromPayload === 'number') {
      return fromPayload;
    }
    if (typeof fromPayload === 'string') {
      const parsed = Number(fromPayload);
      return Number.isFinite(parsed) ? parsed : fromPayload.toLowerCase();
    }
    switch (key) {
      case 'weight':
        return trip.totalWtKg;
      case 'tpCount':
        return getTouchPointCount(trip);
      case 'distance':
        return trip.distKm;
      case 'duration':
        return trip.sortValues?.travelMinutes ?? getTripWindowMetrics(trip).durationMinutes ?? 0;
      case 'tripId':
      default:
        return trip.sortValues?.tripId ?? Number(trip.vehTripNo ?? 0);
    }
  };

  // Sort trips based on sort config
  filteredTrips = [...filteredTrips].sort((a, b) => {
    const aValue = resolveSortMetric(a, sortConfig.key);
    const bValue = resolveSortMetric(b, sortConfig.key);
    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Define sort options mapping for the dropdown
  const sortOptions: CompactSortOption<SortOption>[] = [
    { value: 'tripId', label: 'Trip ID' },
    { value: 'distance', label: 'Distance' },
    { value: 'tpCount', label: 'Touch Points' },
    { value: 'weight', label: 'Weight' },
    { value: 'duration', label: 'Duration' },
  ];

  useEffect(() => {
    if (!timelineSupported || !onTimelineOrderChange) {
      return;
    }
    onTimelineOrderChange(filteredTrips.map((trip) => trip.name));
  }, [filteredTrips, onTimelineOrderChange, timelineSupported]);

  if (!taskInfo) return null;

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      width: '100%',
      position: 'relative',
    }}>
      {/* Search and sort options - at the very top */}
      <Box sx={{ 
        px: 0.8,
        pt: 1,
        pb: 1, 
        borderBottom: '1px solid', 
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mt: 0,
        position: 'relative',
        zIndex: 1, // Ensure search bar is below the close button
      }}>
        <CompactSearchSortControl
          query={searchTerm}
          onQueryChange={onSearch}
          placeholder="Search trips or buyers"
          sortKey={sortConfig.key}
          sortDirection={sortConfig.direction}
          sortOptions={sortOptions}
          onSortKeyChange={onSortKeyChange}
          onSortDirectionChange={onSortDirectionChange}
          leadingAction={
            onToggleAllVisibility ? (
              <Tooltip title={allVisible ? 'Hide all routes' : 'Show all routes'}>
                <IconButton
                  size="small"
                  onClick={() => onToggleAllVisibility(!allVisible)}
                  aria-label={allVisible ? 'Hide all routes' : 'Show all routes'}
                  sx={{
                    width: 30,
                    height: 30,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
                  }}
                >
                  {allVisible ? (
                    <VisibilityIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <VisibilityOffIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Tooltip>
            ) : null
          }
          sx={{ pr: 5.5 }}
        />

        {timelineSupported && onToggleTableTimelines && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 0.65,
              py: 0.35,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ScheduleOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Box sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary' }}>
                Timelines
              </Box>
            </Box>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Switch
                  size="small"
                  checked={showTableTimelines}
                  onChange={(event) => onToggleTableTimelines(event.target.checked)}
                />
              }
              label=""
            />
          </Box>
        )}
      </Box>

      {/* Trip list - scrollable */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          px: 0.75, 
          py: 1,
        }}
      >
        {filteredTrips.map((trip) => (
          <TripCard
            key={trip.name}
            trip={trip}
            tenant={taskInfo?.tenant}
            isSelected={selectedTrips.includes(trip.name)}
            isVisible={visibleTrips.includes(trip.name)}
            isEditSourceTrip={editMode && trip.name === editSourceTrip}
            isHighlighted={highlightedTripName === trip.name}
            selectedEditTouchPointIds={selectedEditTouchPointIds}
            hideDriverContractFields={isGemba}
            onSelect={() => onTripSelect(trip.name)}
            onVisibility={() => onTripVisibility(trip.name)}
            onFocus={(lngLat) => onFocusLngLat && onFocusLngLat(lngLat)}
            onFocusTrip={onFocusTrip ? () => onFocusTrip(trip.name) : undefined}
            onFocusTouchPoint={(tp) => onFocusTouchPoint && onFocusTouchPoint(tp)}
            onHoverTouchPoint={onHoverTouchPoint}
            onToggleEditTouchPoint={onToggleEditTouchPoint}
            onOpenTimeline={
              onOpenTimeline && canBuildTimelineStripData(trip)
                ? () => onOpenTimeline(trip.name)
                : undefined
            }
            onStartTripEdit={onStartTripEdit ? () => onStartTripEdit(trip.name) : undefined}
            onCancelTripEdit={editMode && trip.name === editSourceTrip ? onCancelTripEdit : undefined}
          />
        ))}
        
        {filteredTrips.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4, p: 2 }}>
            No trips found matching your search criteria.
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TripTable;
